import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CompanyContext = createContext();

// Company configurations with Stripe, Auth1, and branding
const COMPANIES = [
  {
    id: 'all',
    name: 'All Companies',
    slug: 'all',
    logo: null,
    color: '#6366f1',
    description: 'Portfolio overview across all companies'
  },
  {
    id: 'babyzilla',
    name: 'BabyZilla',
    slug: 'babyzilla',
    domain: 'babyzilla.ai',
    logo: 'BZ',
    color: '#f472b6',
    accentColor: '#fb7185',
    description: 'AI-powered baby care platform',
    stripeSlug: 'babyzilla',
    stripeAccountId: null,
    auth1TenantSlug: 'babyzilla',
    auth1ApiKey: null,
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  },
  {
    id: 'cachee',
    name: 'Cachee',
    slug: 'cachee',
    domain: 'cachee.ai',
    logo: 'CA',
    color: '#10b981',
    accentColor: '#34d399',
    description: 'AI-powered caching and performance optimization',
    stripeSlug: 'cachee',
    stripeAccountId: null,
    auth1TenantSlug: 'cachee',
    auth1ApiKey: 'auth1_pk_cachee_placeholder',
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  },
  {
    id: 'revmine',
    name: 'RevMine',
    slug: 'revmine',
    domain: 'revmine.ai',
    logo: 'RM',
    color: '#f97316',
    accentColor: '#ec4899',
    description: 'Revenue intelligence and deal optimization',
    stripeSlug: 'revmine',
    stripeAccountId: null,
    auth1TenantSlug: 'revmine',
    auth1ApiKey: 'auth1_pk_8566b9bfd3cc33eae8f35aff3735b88815168431abb40457',
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  },
  {
    id: 'v100',
    name: 'V100',
    slug: 'v100',
    domain: 'v100.ai',
    logo: 'V1',
    color: '#3b82f6',
    accentColor: '#60a5fa',
    description: 'AI voice agent platform',
    stripeSlug: 'v100',
    stripeAccountId: null,
    auth1TenantSlug: 'v100',
    auth1ApiKey: null,
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  },
  {
    id: 'mirror100',
    name: 'Mirror100',
    slug: 'mirror100',
    domain: 'mirror100.ai',
    logo: 'M1',
    color: '#8b5cf6',
    accentColor: '#a78bfa',
    description: 'AI mirror and reflection platform',
    stripeSlug: 'mirror100',
    stripeAccountId: null,
    auth1TenantSlug: 'mirror100',
    auth1ApiKey: null,
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  },
  {
    id: 'chat100',
    name: 'Chat100.ai',
    slug: 'chat100',
    domain: 'chat100.ai',
    logo: 'C1',
    color: '#6366f1',
    accentColor: '#a855f7',
    description: 'AI-powered conversational platform',
    stripeSlug: null,
    stripeAccountId: null,
    auth1TenantSlug: 'chat100',
    auth1ApiKey: 'auth1_pk_chat100_3910593125ec8c06e4414f5b31fa762a',
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  },
  {
    id: 'l100',
    name: 'L100.ai',
    slug: 'l100',
    domain: 'l100.ai',
    logo: 'L1',
    color: '#14b8a6',
    accentColor: '#2dd4bf',
    description: 'AI learning platform',
    stripeSlug: null,
    stripeAccountId: null,
    auth1TenantSlug: 'l100',
    auth1ApiKey: null,
    metrics: {
      revenue: { mrr: 0, arr: 0, growth: 0 },
      users: { total: 0, active: 0, new: 0 },
      auth: { logins: 0, sessions: 0, fraudAlerts: 0 },
      email: { sent: 0, delivered: 0, opened: 0, clicked: 0 }
    }
  }
];

export const CompanyProvider = ({ children }) => {
  const [currentCompanyId, setCurrentCompanyId] = useState(() => {
    return localStorage.getItem('z101_selected_company') || 'all';
  });
  const [companies, setCompanies] = useState(COMPANIES);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist selection to localStorage
  useEffect(() => {
    localStorage.setItem('z101_selected_company', currentCompanyId);
  }, [currentCompanyId]);

  // Get current company object
  const currentCompany = companies.find(c => c.id === currentCompanyId) || companies[0];

  // Is viewing a specific company (not "all")
  const isCompanyView = currentCompanyId !== 'all';

  // Select a company
  const selectCompany = useCallback((companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setCurrentCompanyId(companyId);
    }
  }, [companies]);

  // Exit to portfolio view
  const exitCompanyView = useCallback(() => {
    setCurrentCompanyId('all');
  }, []);

  // Get company by ID
  const getCompanyById = useCallback((id) => {
    return companies.find(c => c.id === id);
  }, [companies]);

  // Get all companies (excluding "all" option)
  const getAllCompanies = useCallback(() => {
    return companies.filter(c => c.id !== 'all');
  }, [companies]);

  // Update company metrics (called after API fetch)
  const updateCompanyMetrics = useCallback((companyId, metrics) => {
    setCompanies(prev => prev.map(c =>
      c.id === companyId ? { ...c, metrics: { ...c.metrics, ...metrics } } : c
    ));
  }, []);

  // Update Stripe account connection
  const connectStripeAccount = useCallback((companyId, stripeAccountId) => {
    setCompanies(prev => prev.map(c =>
      c.id === companyId ? { ...c, stripeAccountId } : c
    ));
  }, []);

  // Get aggregated metrics across all companies
  const getAggregatedMetrics = useCallback(() => {
    const activeCompanies = companies.filter(c => c.id !== 'all');
    return {
      totalRevenue: activeCompanies.reduce((sum, c) => sum + (c.metrics?.revenue?.mrr || 0), 0),
      totalUsers: activeCompanies.reduce((sum, c) => sum + (c.metrics?.users?.total || 0), 0),
      totalEmailsSent: activeCompanies.reduce((sum, c) => sum + (c.metrics?.email?.sent || 0), 0),
      activeCompanies: activeCompanies.length,
      companiesWithStripe: activeCompanies.filter(c => c.stripeAccountId).length
    };
  }, [companies]);

  return (
    <CompanyContext.Provider value={{
      // Current state
      currentCompany,
      currentCompanyId,
      isCompanyView,
      companies,
      isLoading,
      error,

      // Actions
      selectCompany,
      exitCompanyView,
      getCompanyById,
      getAllCompanies,
      updateCompanyMetrics,
      connectStripeAccount,
      getAggregatedMetrics
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export default CompanyContext;
